import { useEffect, useState } from "react";
import {
  BENCHMARKS,
  BENCHMARK_PROVENANCE,
  HEADLINE_BENCHMARK,
  PEAK_THROUGHPUT,
  UNCACHED_BENCHMARK,
  UNTUNED_BENCHMARK,
  allowanceFor,
  formatDbBytes,
  scannedShare,
} from "@saucer/core";
import type { BenchmarkRun } from "@saucer/core";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  ChartLineData01Icon,
  CloudServerIcon,
  DatabaseIcon,
  FlashIcon,
  LockKeyIcon,
  SourceCodeIcon,
} from "@hugeicons/core-free-icons";
import { go, SaasFooter, SaasNav, useReveal } from "../components/saas-shell.js";
import type { Session } from "../app.js";

/**
 * Managed databases, built from the landing's own components rather than a
 * parallel set of its own.
 *
 * That is the whole point of this file: the same `saas-hero`, the same framed
 * dark showcase, the same twelve-column bento with its hairline seams, the same
 * pricing card, accordion and closing card. A product page that invents its own
 * grid and its own section header reads as a different site, however good the
 * pieces are on their own.
 *
 * Every figure comes from `@saucer/core/benchmarks`, which carries the hardware
 * and the method with it, and every cached figure is labelled cached. A headline
 * latency that quietly dropped its cache would be the exact thing the benchmark
 * section exists to prevent.
 */
export function Managed({ session }: { session: Session }) {
  useReveal();
  const start = session ? "/databases" : "/login";

  return (
    <div className="landing saas">
      <SaasNav />

      <section className="saas-hero">
        <span className="saas-beta">
          <i aria-hidden="true" /> New · ClickHouse and Postgres
        </span>
        <h1 className="saas-hero__title">
          Your SQL becomes{" "}
          <span className="landing__url" aria-label="api.saucer.sh/v0/pipes/anything">
            api.saucer.sh/v0/pipes/<CyclingPipe />
          </span>
        </h1>
        <p className="saas-hero__lede">
          Spin up ClickHouse or Postgres in seconds. Land events, shape them with SQL, publish the
          query as an HTTP endpoint — answered on the machine the data sits on.
        </p>
        <div className="saas-actions">
          <a className="pill pill--primary" href={start} onClick={go(start)}>
            {session ? "Create a database" : "Start free"}
          </a>
          <a className="saas-textlink" href="/managed/docs" onClick={go("/managed/docs")}>
            Read the docs →
          </a>
        </div>
      </section>

      <div className="saas-wrap-5xl">
        <div className="saas-art">
          <div className="saas-frame">
            <PipeShowcase />
          </div>
        </div>
      </div>

      <Features />
      <Engines />
      <Benchmarks />
      <PricingCta start={start} />

      <section className="saas-faq" id="faq">
        <span className="saas-chip">FAQ</span>
        <h2 className="saas-h2">
          Frequently
          <br />
          asked questions
        </h2>
        <p className="saas-lede">Answers about the engines, the endpoints and the numbers.</p>

        <div className="saas-accordion">
          {FAQ.map((item) => (
            <details key={item.q} className="saas-accordion__item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="saas-cta">
        <div className="saas-wrap-5xl">
          <div className="saas-cta__card">
            <h2 className="saas-cta__title">
              Stop building
              <br />
              the same API
            </h2>
            <p className="saas-lede">
              Your data is already good. Give it an endpoint that answers in a millisecond.
            </p>
            <div className="saas-actions">
              <a className="pill pill--primary" href={start} onClick={go(start)}>
                {session ? "Create a database" : "Start free"}
              </a>
              <a className="pill pill--soft" href="/managed/docs" onClick={go("/managed/docs")}>
                See the docs
              </a>
            </div>
            <span className="saas-micro">Postgres and ClickHouse · both on the free plan</span>
          </div>
        </div>
      </section>

      <SaasFooter />
    </div>
  );
}

/** The landing cycles a username in its headline; this cycles a pipe name. */
const PIPE_NAMES = ["top_urls", "revenue", "signups", "session_stats"];

function CyclingPipe() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // Reduced motion means a headline that holds still, not one that moves
    // slower — a cycling word is exactly what that setting is asking about.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setInterval(
      () => setIndex((current) => (current + 1) % PIPE_NAMES.length),
      2200,
    );
    return () => window.clearInterval(id);
  }, []);

  const name = PIPE_NAMES[index]!;
  // Same correction the landing makes: `ch` ignores the display type's
  // negative tracking, so it is subtracted per glyph or the box jitters.
  const width = `calc(${name.length}ch - ${(name.length * 0.045).toFixed(3)}em)`;

  return (
    <em className="landing__you" style={{ width }}>
      <span key={index} className="landing__you-word">
        {name}
      </span>
    </em>
  );
}

/* -- the framed showcase --------------------------------------------------- */

const DEMOS = [
  {
    id: "ingest",
    label: "ingest",
    name: "events — curl",
    lines: [
      { text: "$ curl -X POST 'api.saucer.sh/v0/events?name=hits' \\", tone: "" },
      { text: "    -H 'Authorization: Bearer $TOKEN' \\", tone: "" },
      { text: "    --data-binary @events.ndjson", tone: "" },
      { text: '{"successful_rows":12480,"quarantined_rows":0}', tone: "muted" },
    ],
  },
  {
    id: "pipe",
    label: "pipe",
    name: "session_stats.sql",
    lines: [
      { text: "SELECT toStartOfHour(timestamp) AS hour,", tone: "" },
      { text: "       count() AS hits", tone: "" },
      { text: "FROM hits", tone: "" },
      { text: "WHERE country = {{String(country, 'US')}}", tone: "dim" },
      { text: "  AND timestamp >= now() - toIntervalHour(24)", tone: "" },
      { text: "GROUP BY hour ORDER BY hour DESC", tone: "" },
    ],
  },
  {
    id: "endpoint",
    label: "endpoint",
    name: "GET /v0/pipes/session_stats.json",
    lines: [
      { text: "$ curl 'api.saucer.sh/v0/pipes/session_stats.json?country=DE'", tone: "" },
      { text: '{"data":[{"hour":"2026-08-23 10:00:00","hits":"20841"}],', tone: "" },
      { text: '  "rows":25,', tone: "" },
      { text: '  "statistics":{"elapsed":0.007,"rows_read":16384}}', tone: "muted" },
      { text: "x-saucer-cache: hit   x-saucer-db-ms: 0.26", tone: "dim" },
    ],
  },
] as const;

/**
 * The dark card under the hero, in the same frame the landing puts its CLI
 * demo in. Tabbed rather than animated: the landing's terminal types itself
 * because a file upload is a moment in time, whereas these three are one
 * pipeline and the reader wants to move between them.
 */
function PipeShowcase() {
  const [index, setIndex] = useState(0);
  const demo = DEMOS[index]!;

  return (
    <div className="demo">
      <div className="demo__tabs" role="tablist" aria-label="Pipeline examples">
        {DEMOS.map((entry, i) => (
          <button
            key={entry.id}
            role="tab"
            aria-selected={i === index}
            className={`demo__tab${i === index ? " demo__tab--active" : ""}`}
            type="button"
            onClick={() => setIndex(i)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="terminal" role="tabpanel">
        <div className="terminal__bar" aria-hidden="true">
          <span className="terminal__dots">
            <i />
            <i />
            <i />
          </span>
          <span className="terminal__name mono">{demo.name}</span>
        </div>
        <pre className="terminal__body">
          {demo.lines.map((line, i) => (
            <span
              key={i}
              className={`terminal__line${line.tone ? ` terminal__line--${line.tone}` : ""}`}
            >
              {line.text}
            </span>
          ))}
        </pre>
      </div>
    </div>
  );
}

/* -- the bento ------------------------------------------------------------- */

function CellHead({ icon, label }: { icon: IconSvgElement; label: string }) {
  return (
    <div className="bento__head">
      <span className="bento__icon" aria-hidden="true">
        <HugeiconsIcon icon={icon} size={24} strokeWidth={1.7} />
      </span>
      <span className="bento__label">{label}</span>
    </div>
  );
}

const METRICS = [
  { icon: FlashIcon, value: `${HEADLINE_BENCHMARK.p50} ms`, label: "median, cached endpoint" },
  { icon: DatabaseIcon, value: `${UNCACHED_BENCHMARK.p50} ms`, label: "median, no cache at all" },
  {
    icon: ChartLineData01Icon,
    value: `${Math.round(PEAK_THROUGHPUT.requestsPerSecond / 1000)}k/s`,
    label: "requests on one instance",
  },
  { icon: CloudServerIcon, value: "16,384", label: "rows read of fifty million" },
];

function Features() {
  return (
    <section className="saas-features" id="features">
      <div className="saas-wrap-5xl">
        <div className="saas-sectionhead">
          <span className="saas-chip">Why managed</span>
          <h2 className="saas-h2">
            A database.
            <br />
            And the API on top.
          </h2>
          <p className="saas-lede">
            The database is the easy half. The service, the auth, the caching and the metrics are
            the part you would otherwise write yourself.
          </p>
        </div>

        <div className="bento">
          <article className="bento__cell bento__cell--feature">
            <CellHead icon={FlashIcon} label="Endpoints" />
            <h3 className="bento__title">
              Publish a query, get an API that answers in{" "}
              <em>{HEADLINE_BENCHMARK.p50}&nbsp;ms</em>
            </h3>
            <p className="bento__copy">
              A pipe is a chain of SQL nodes; publish one and it becomes a parameterised HTTP
              endpoint with its own scoped tokens. Parameters are typed, so a caller's string is
              parsed into its declared type and never reaches the SQL.
            </p>
            <ul className="bento__tags">
              <li className="bento__tag mono">typed params</li>
              <li className="bento__tag mono">scoped tokens</li>
              <li className="bento__tag mono">opt-in cache</li>
            </ul>
            <div className="bento__bars">
              <Bar
                label="cached"
                value={`${HEADLINE_BENCHMARK.p50} ms`}
                ratio={HEADLINE_BENCHMARK.p50 / UNTUNED_BENCHMARK.p50}
                warm
              />
              <Bar
                label="uncached"
                value={`${UNCACHED_BENCHMARK.p50} ms`}
                ratio={UNCACHED_BENCHMARK.p50 / UNTUNED_BENCHMARK.p50}
                warm={false}
              />
              <Bar
                label="before tuning"
                value={`${UNTUNED_BENCHMARK.p50} ms`}
                ratio={1}
                warm={false}
              />
            </div>
          </article>

          <article className="bento__cell">
            <CellHead icon={DatabaseIcon} label="Data sources" />
            <h3 className="bento__title">Ingest that survives real traffic</h3>
            <p className="bento__copy">
              POST newline-delimited JSON. Inserts are batched server-side, so a burst is a batch
              rather than a thousand parts the merge scheduler never catches up with.
            </p>
            <code className="bento__snippet mono">
              <span className="bento__snippet-prompt" aria-hidden="true">
                $
              </span>
              curl -d @events.ndjson …/v0/events?name=hits
            </code>
          </article>

          <article className="bento__cell">
            <CellHead icon={ChartLineData01Icon} label="OpenTelemetry" />
            <h3 className="bento__title">Point your exporter at it. That is the setup.</h3>
            <p className="bento__copy">
              Traces, metrics and logs arrive over OTLP on the paths the spec defines — no
              collector to run, no schema to declare. They land as tables you can query from a
              pipe and publish an endpoint over.
            </p>
            <code className="bento__snippet mono">
              <span className="bento__snippet-prompt" aria-hidden="true">
                $
              </span>
              OTEL_EXPORTER_OTLP_ENDPOINT=https://api.saucer.sh
            </code>
          </article>

          <article className="bento__cell bento__cell--wide">
            <CellHead icon={LockKeyIcon} label="Isolation" />
            <h3 className="bento__title">Your SQL cannot leave your database</h3>
            <p className="bento__copy">
              Every statement runs as a service account granted on your database alone, so a query
              naming another tenant's table is refused by the engine itself.
            </p>
          </article>

          {METRICS.map((metric) => (
            <article key={metric.label} className="bento__cell bento__cell--metric">
              <span className="bento__icon" aria-hidden="true">
                <HugeiconsIcon icon={metric.icon} size={24} strokeWidth={1.7} />
              </span>
              <strong className="bento__metric">{metric.value}</strong>
              <span className="bento__metric-label">{metric.label}</span>
            </article>
          ))}
        </div>

        <p className="saas-micro saas-center">
          measured on the box that serves it — {BENCHMARK_PROVENANCE.hardware}
        </p>
      </div>
    </section>
  );
}

function Bar({
  label,
  value,
  ratio,
  warm,
}: {
  label: string;
  value: string;
  ratio: number;
  warm: boolean;
}) {
  return (
    <div className="bento__bar">
      <span className="bento__bar-label">{label}</span>
      <span className="bento__bar-value">{value}</span>
      <span className="bento__bar-track">
        <i
          className={warm ? "bento__bar-fill bento__bar-fill--warm" : "bento__bar-fill"}
          // A floor, or the fastest bar — the one that makes the argument — is
          // a sliver nobody can see.
          style={{ "--w": Math.max(0.02, ratio) } as React.CSSProperties}
        />
      </span>
    </div>
  );
}

/* -- the two engines, as a second bento ------------------------------------ */

function Engines() {
  return (
    <section className="saas-features" id="engines">
      <div className="saas-wrap-5xl">
        <div className="saas-sectionhead">
          <span className="saas-chip">Two engines</span>
          <h2 className="saas-h2">
            Pick the one
            <br />
            the question wants.
          </h2>
          <p className="saas-lede">
            Both provisioned the same way, both on the same metal, both with the password shown
            once and rotatable whenever you like.
          </p>
        </div>

        <div className="bento">
          <article className="bento__cell bento__cell--half">
            <CellHead icon={ChartLineData01Icon} label="ClickHouse" />
            <h3 className="bento__title">Columnar, for the questions Postgres takes minutes over</h3>
            <p className="bento__copy">
              The whole endpoint product lives here: data sources, pipes, published APIs, and the
              rows-read figure on every single response.
            </p>
            <code className="bento__snippet mono">
              <span className="bento__snippet-prompt" aria-hidden="true">
                $
              </span>
              clickhouse-client --host ch.saucer.sh --port 9440 --secure
            </code>
          </article>

          <article className="bento__cell bento__cell--half">
            <CellHead icon={SourceCodeIcon} label="Postgres" />
            <h3 className="bento__title">Real Postgres 18, with nothing in the way</h3>
            <p className="bento__copy">
              psql, Drizzle, Prisma, pgAdmin — anything that speaks the wire protocol. No proxy
              dialect to learn. TLS required, plaintext refused outright.
            </p>
            <code className="bento__snippet mono">
              <span className="bento__snippet-prompt" aria-hidden="true">
                $
              </span>
              psql &quot;postgresql://you@pg.saucer.sh/you?sslmode=require&quot;
            </code>
          </article>
        </div>
      </div>
    </section>
  );
}

/* -- benchmarks ------------------------------------------------------------ */

function Benchmarks() {
  const [open, setOpen] = useState(false);
  const slowest = Math.max(...BENCHMARKS.map((run) => run.p99));

  return (
    <section className="saas-features" id="benchmarks">
      <div className="saas-wrap-5xl">
        <div className="saas-sectionhead">
          <span className="saas-chip">Benchmarks</span>
          <h2 className="saas-h2">
            What every shape
            <br />
            actually costs.
          </h2>
          <p className="saas-lede">
            Uncached, latency tracks rows scanned and almost nothing else. Cached, it stops being
            a database question at all.
          </p>
        </div>

        <div className="btab">
          <div className="btab__scroll">
            <table className="btab__table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th className="btab__num">Rows read</th>
                  <th className="btab__num">Conc.</th>
                  <th className="btab__num">p50</th>
                  <th className="btab__num">p95</th>
                  <th className="btab__num">p99</th>
                  <th className="btab__num">req/s</th>
                  <th className="btab__vis">p99</th>
                </tr>
              </thead>
              <tbody>
                {BENCHMARKS.map((run) => (
                  <Row
                    key={`${run.endpoint}-${run.concurrency}-${run.cached}`}
                    run={run}
                    slowest={slowest}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="btab__foot">
          <button className="pill pill--soft" type="button" onClick={() => setOpen((was) => !was)}>
            {open ? "Hide the method" : "How this was measured"}
          </button>
          {open ? (
            <dl className="btab__method">
              <Detail term="Measured" value={BENCHMARK_PROVENANCE.measuredAt} />
              <Detail term="Hardware" value={BENCHMARK_PROVENANCE.hardware} />
              <Detail term="Allowed" value={BENCHMARK_PROVENANCE.resources} />
              <Detail term="ClickHouse" value={BENCHMARK_PROVENANCE.clickhouseVersion} />
              <Detail
                term="Dataset"
                value={`${BENCHMARK_PROVENANCE.dataset}, ${BENCHMARK_PROVENANCE.datasetOnDisk}`}
              />
              <Detail term="Sorting key" value={BENCHMARK_PROVENANCE.sortingKey} />
              <Detail term="Method" value={BENCHMARK_PROVENANCE.method} />
            </dl>
          ) : null}
          <p className="btab__caveat">
            Rows marked <em>cached</em> had a cache lifetime set, so the result may be up to that
            old. Caching is off unless an endpoint asks for it. {BENCHMARK_PROVENANCE.caveat}
          </p>
        </div>
      </div>
    </section>
  );
}

function Row({ run, slowest }: { run: BenchmarkRun; slowest: number }) {
  const share = scannedShare(run);
  // Log scale: the range is 0.28 ms to 206 ms, and a linear bar renders every
  // fast row as an invisible sliver — the opposite of the point.
  const width = (Math.log10(run.p99 + 1) / Math.log10(slowest + 1)) * 100;

  return (
    <tr>
      <td>
        <span className="btab__ep mono">{run.endpoint}</span>
        {run.cached ? <span className="btab__chip mono">cached</span> : null}
        <span className="btab__shape">{run.shape}</span>
      </td>
      <td className="btab__num mono">
        {run.rowsScanned.toLocaleString()}
        <span className="btab__share">{(share * 100).toFixed(share < 0.01 ? 3 : 0)}%</span>
      </td>
      <td className="btab__num mono">{run.concurrency}</td>
      <td className="btab__num mono btab__p50">{run.p50}</td>
      <td className="btab__num mono">{run.p95}</td>
      <td className="btab__num mono">{run.p99}</td>
      <td className="btab__num mono">{run.requestsPerSecond.toLocaleString()}</td>
      <td className="btab__vis">
        <span className="btab__bar" aria-hidden="true">
          <span
            className={`btab__bar-fill${run.cached ? " btab__bar-fill--fast" : ""}`}
            style={{ inlineSize: `${Math.max(2, width)}%` }}
          />
        </span>
        <span className="visually-hidden">p99 {run.p99} milliseconds</span>
      </td>
    </tr>
  );
}

function Detail({ term, value }: { term: string; value: string }) {
  return (
    <div className="btab__method-row">
      <dt className="mono">{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/* -- pricing, in the landing's own card ------------------------------------ */

function PricingCta({ start }: { start: string }) {
  const free = allowanceFor("free", "clickhouse");
  const pro = allowanceFor("pro", "clickhouse");

  return (
    <section className="saas-pricing" id="pricing">
      <div className="pcta">
        <div className="pcta__row">
          <div className="pcta__lead">
            <span className="saas-chip">Pricing</span>
            <h2 className="pcta__title">
              Start free. {formatDbBytes(free.storageBytes)} of ClickHouse, no card.
            </h2>
            <p className="pcta__lede">
              A Postgres database on the same plan. Pro is $10 a month for{" "}
              {formatDbBytes(pro.storageBytes)} per workspace, {pro.shared} of them, or a
              dedicated container with {pro.dedicatedCpus} CPUs of its own.
            </p>
          </div>

          <div className="pcta__actions">
            <a className="pill pill--primary" href={start} onClick={go(start)}>
              Start free
            </a>
            <a className="pcta__compare" href="/managed/docs" onClick={go("/managed/docs")}>
              Read the docs →
            </a>
          </div>
        </div>

        <p className="pcta__enterprise">
          Dedicated instances and larger quotas are available — the limits above are the defaults,
          not the ceiling.
        </p>
      </div>
    </section>
  );
}

const FAQ = [
  {
    q: "What is a pipe, exactly?",
    a: "A chain of SQL nodes. Each node can select from your data sources and from any earlier node by name, so a hard query stays readable. They compile to a single statement with CTEs when the pipe runs, so the chain costs nothing at query time.",
  },
  {
    q: "How are parameters safe?",
    a: "They are typed. {{Int64(limit, 10)}} is parsed into an Int64 and rendered from the parsed value, so a caller's string never reaches the SQL. There is no raw or untyped parameter, and there will not be one — passing limit=10;DROP TABLE hits returns a 400 saying it is not a valid Int64.",
  },
  {
    q: "What does the cache actually trade?",
    a: "Freshness. A cached endpoint answers from memory in a fraction of a millisecond, and the result can be up to its lifetime old. It is off by default and set per endpoint, because only you know whether your dashboard can be a few seconds behind. Every response says x-saucer-cache: hit or miss.",
  },
  {
    q: "Why is my endpoint slow?",
    a: "Look at rows_read in its own response first. Latency tracks rows scanned and almost nothing else — an endpoint that got slower has nearly always started reading more, usually because a filter moved off the sorting key's leading columns.",
  },
  {
    q: "Can I just use it as a normal database?",
    a: "Yes. Postgres is real Postgres 18 on pg.saucer.sh and ClickHouse speaks its native protocol on ch.saucer.sh — point psql or clickhouse-client at either. TLS is required on both; plaintext is refused with a message telling you so.",
  },
];
